import React, { useState, useEffect } from 'react';
import { Container, Typography, Grid, Paper, Box, CircularProgress } from '@mui/material';
import {
    Event as EventIcon,
    People as PeopleIcon,
    CardGiftcard as SpreadIcon,
    Announcement as AnnouncementIcon
} from '@mui/icons-material';
import { appointmentService } from '../../services/appointmentService';
import { spreadsService } from '../../services/spreadsService';
import { announcementService } from '../../services/announcementService';
import { authService } from '../../services/authService';

interface DashboardStats {
    appointments: number;
    users: number;
    spreads: number;
    announcements: number;
}

const DashboardCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
}> = ({ title, value, icon }) => (
    <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ color: 'primary.main', mr: 1 }}>{icon}</Box>
            <Typography variant="h6" component="h2">
                {title}
            </Typography>
        </Box>
        <Typography variant="h4" component="p">
            {value}
        </Typography>
    </Paper>
);

const AdminDashboard: React.FC = () => {
    const [stats, setStats] = useState<DashboardStats>({
        appointments: 0,
        users: 0,
        spreads: 0,
        announcements: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadStats = async () => {
            try {
                setLoading(true);
                
                // Tüm istatistikleri paralel olarak yükle
                const [appointments, spreads, announcements] = await Promise.all([
                    appointmentService.getAll(),
                    spreadsService.getAll(),
                    announcementService.getActiveAnnouncements()
                ]);

                // Kullanıcı sayısını al
                const userCount = await authService.getUserCount();

                setStats({
                    appointments: appointments.length,
                    users: userCount || 0,
                    spreads: spreads.length,
                    announcements: announcements.length
                });
            } catch (error) {
                // Handle error silently
            } finally {
                setLoading(false);
            }
        };

        loadStats();
    }, []);

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Yönetim Paneli
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="Randevular"
                        value={stats.appointments}
                        icon={<EventIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="Kullanıcılar"
                        value={stats.users}
                        icon={<PeopleIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="Fallar"
                        value={stats.spreads}
                        icon={<SpreadIcon />}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="Aktif Duyurular"
                        value={stats.announcements}
                        icon={<AnnouncementIcon />}
                    />
                </Grid>
            </Grid>
        </Container>
    );
};

export default AdminDashboard; 